import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../services/supabase/supabase';

/**
 * Hook to limit the number of concurrent connections per owner using Supabase Presence.
 * 
 * @param {string} ownerId - The ID of thetournament owner
 * @param {number} limit - The maximum allowed number of connections
 * @returns {object} { isLimitExceeded, currentCount, limit }
 */
export const useConnectionLimiter = (ownerId, limit) => {
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const [currentCount, setCurrentCount] = useState(0);
  const [myConnectionId] = useState(() => crypto.randomUUID());

  // Use a ref for joinedAt to ensure it persists across renders without being a dependency
  // We use Date.now() + random suffix to minimize collision probability
  const joinedAtRef = useRef(Date.now() + Math.random());

  useEffect(() => {
    if (!ownerId) return;

    // Presence channel name: presence:owner_{ownerId}
    const channelName = `presence:owner_${ownerId}`;

    const channel = supabase.channel(channelName, {
      config: {
        presence: {
          key: myConnectionId,
        },
      },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const newState = channel.presenceState();

        // Flatten the state to get a list of all connected clients
        // Each entry in presenceState is an array of presence objects for a given key
        const clients = [];

        Object.values(newState).forEach(presences => {
          presences.forEach(presence => {
            clients.push(presence);
          });
        });

        // Update count
        setCurrentCount(clients.length);

        // Logic to check if WE are exceeding the limit
        // 1. Sort clients by joinedAt (ASC) - First come, first served
        // Use a robust sort: primary by joinedAt, secondary by connectionId (lexicographical) for deterministic tie-breaking
        clients.sort((a, b) => {
          if (a.joinedAt !== b.joinedAt) {
            return a.joinedAt - b.joinedAt;
          }
          return a.connectionId.localeCompare(b.connectionId);
        });

        // 2. Find my index
        const myIndex = clients.findIndex(c => c.connectionId === myConnectionId);

        // 3. Check if my index is outside the limit
        // Indices are 0-based. If limit is 2, allowed indices are 0 and 1.
        if (myIndex >= limit) {
          setIsLimitExceeded(true);
        } else {
          setIsLimitExceeded(false);
        }
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Track our presence immediately upon subscription
          await channel.track({
            connectionId: myConnectionId,
            joinedAt: joinedAtRef.current,
            // Add other metadata if needed
          });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [ownerId, limit, myConnectionId]);

  return { isLimitExceeded, currentCount, limit };
};
